
import { FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2 } from 'lucide-react';
// Label import removed - not used in this component
import { getAvailableRoleOptions } from './UserDialogShared';
import { NO_COMPANY_SENTINEL, type CompanyRoleSectionProps } from './UserDialogTypes';
import { PermissionGate } from '@/components/permissions';

export function CompanyRoleSection({
  form,
  isSubmittingForm,
  isEdit: _isEdit,
  companies,
  companiesLoading,
  userContext,
  userToEdit: _userToEdit,
  canEditProfileFields,
  shouldShowStatusField,
  shouldMakeRoleEditable,
  t
}: CompanyRoleSectionProps) {
  const roleOptions = getAvailableRoleOptions(userContext);

  // Helper to determine if field should be disabled
  const isFieldDisabled = (fieldName: string) => {
    if (isSubmittingForm) return true;

    if (fieldName === 'app_role') {
      return !shouldMakeRoleEditable;
    }

    return !canEditProfileFields;
  };

  return (
    <>
      {/* Company Selection - Show ONLY for System Admins (not Company Admins) */}
      {(userContext?.is_system_admin || userContext?.app_role === 'ADMIN') && (
        companiesLoading ? (
          <div className="space-y-2 transition-opacity duration-300 ease-in-out opacity-75">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-16" />
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{t('usersActionDialog.loading.companies')}</span>
            </div>
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        ) : companies && companies.length > 0 ? (
          <div className="transition-opacity duration-300 ease-in-out opacity-100">
            <FormField
              control={form.control}
              name="companyId"
              render={({ field }) => (
                <FormItem>
                <FormLabel>{t('usersActionDialog.labels.company')}</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value === NO_COMPANY_SENTINEL ? null : value);
                  }}
                  value={field.value || NO_COMPANY_SENTINEL}
                  disabled={isFieldDisabled('companyId')}
                >
                  <FormControl>
                    <SelectTrigger data-testid="company-select">
                      <SelectValue placeholder={t('usersActionDialog.placeholders.company')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {/* System Admins can assign users to no company */}
                    <PermissionGate requires="admin.assign_no_company" fallback="hidden">
                      <SelectItem value={NO_COMPANY_SENTINEL} data-testid="no-company-option">
                        {t('usersActionDialog.options.noCompany')}
                      </SelectItem>
                    </PermissionGate>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id} data-testid={`company-option-${company.id}`}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          </div>
        ) : null
      )}

      {/* Role Selection */}
      <FormField
        control={form.control}
        name="app_role"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('usersActionDialog.labels.role')}</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value || 'USER'}
              disabled={isFieldDisabled('app_role')}
            >
              <FormControl>
                <SelectTrigger data-testid="role-select">
                  <SelectValue placeholder={t('common.select')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {roleOptions.map((role) => (
                  <SelectItem key={role.value} value={role.value} data-testid={`role-option-${role.value}`}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription>
              {field.value === 'ADMIN' && t('usersActionDialog.descriptions.role')}
              {field.value === 'COMPANY_ADMIN' && t('usersActionDialog.descriptions.role')}
              {field.value === 'USER' && t('usersActionDialog.descriptions.role')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Status Field - Show based on shouldShowStatusField */}
      {shouldShowStatusField && (
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('usersActionDialog.labels.status')}</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value || ''}
                disabled={isFieldDisabled('status')}
              >
                <FormControl>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder={t('common.select')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="ACTIVE" data-testid="status-active">
                    {t('usersTable.status.active')}
                  </SelectItem>
                  <SelectItem value="INACTIVE" data-testid="status-inactive">
                    {t('usersTable.status.inactive')}
                  </SelectItem>
                  <SelectItem value="PENDING" data-testid="status-pending">
                    {t('usersTable.status.pending')}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* Representative Toggle - Show based on canEditProfileFields */}
      {canEditProfileFields && (
        <FormField
          control={form.control}
          name="isRepresentative"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  {t('usersActionDialog.labels.companyRepresentative')}
                </FormLabel>
                <FormDescription>
                  {t('usersActionDialog.descriptions.role')}
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value || false}
                  onCheckedChange={field.onChange}
                  disabled={isSubmittingForm}
                  data-testid="representative-toggle"
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}

      {/* Loading State for Companies - Only for System Admins */}
      {(userContext?.is_system_admin || userContext?.app_role === 'ADMIN') && companiesLoading && (
        <div className="text-sm text-gray-500">
          {t('usersActionDialog.loading.companies')}
        </div>
      )}
    </>
  );
}